import React, { useEffect } from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as reportInitialState } from '~redux/slices/report';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import { FundTypeEnum, MetalTypeEnum, ReportInventoryReportSelectedTab } from '~utils/enums';

import * as Styles from './InventoryReportSearchAndFilterBar.module.scss';
import { TGetInventoryReportTransactionListingFilters } from '~types/feat/report';
import dayjs from 'dayjs';

const InventoryReportSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();
    const filters = useAppSelector(state => state.report.inventoryReport.inventoryReceivedReportTransactionListing.filters);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);
    const initialFilters = reportInitialState.inventoryReport.inventoryReceivedReportTransactionListing.filters;
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const productList = useAppSelector((state) => state.gold.allProducts);

    const selectedInventoryReportTab = useAppSelector(state => state.report.inventoryReport.selectedInventoryReportTab);

    const isBranchInventoryTab = selectedInventoryReportTab === ReportInventoryReportSelectedTab.BranchInventory;

    const setFilters = (inputFilters: TGetInventoryReportTransactionListingFilters) => {
        dispatch(combinedActions.reportSetInventoryReportTransactionListingFilters(inputFilters));
    };
    const handleSearchClick = () => {
        if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived) {
            dispatch(combinedActions.reportGetInventoryReceivedReportTransactionListingAttempt());
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered) {
            dispatch(combinedActions.reportGetInventoryDeliveredReportTransactionListingAttempt());
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.BranchInventory) {
            dispatch(combinedActions.reportGetInventoryBranchReportListingAttempt());
        }
    };
    const handleCancelClick = () => {
        const resetFilters = {
            ...initialFilters,
            branchName: !allBranchAccess
                ? (branchesList.find(branch => branch.branchCode === branchCode)?.branchName || '')
                : '',
        };

        setFilters(resetFilters);
        if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryReceived) {
            dispatch(combinedActions.reportGetInventoryReceivedReportTransactionListingAttempt());
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.InventoryDelivered) {
            dispatch(combinedActions.reportGetInventoryDeliveredReportTransactionListingAttempt());
        } else if (selectedInventoryReportTab === ReportInventoryReportSelectedTab.BranchInventory) {
            dispatch(combinedActions.reportGetInventoryBranchReportListingAttempt());
        }
    };

    useEffect(() => {
        if (branchesList.length === 0) return;

        if (allBranchAccess) {
            setFilters({
                ...filters,
                branchName: '',
                branchCode: '',
            });
            return;
        }

        const defaultBranch = branchesList.find(b => b.branchCode === branchCode);

        if (!defaultBranch) return;

        setFilters({
            ...filters,
            branchName: defaultBranch.branchName,
            branchCode: defaultBranch.branchCode,
        });
    }, [branchesList]);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];

        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const massagedProductsListOption = () => {
        if (!productList.length) return [];
        return productList.map((product) => ({
            label: product.productName,
            value: product.productId,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: isBranchInventoryTab ? '' : filters.dateFrom,
                setDate: (date: string) => {
                    if (isBranchInventoryTab) return;

                    if (date > filters.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else {
                        setFilters({ ...filters, dateFrom: date });
                    }
                },
                startDate: isBranchInventoryTab ? '' : filters.dateFrom,
                endDate: isBranchInventoryTab ? '' : filters.dateTo,
                selectsStart: true,
                maxDate: dayjs().toISOString(),
                disabled: isBranchInventoryTab,
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: isBranchInventoryTab ? '' : filters.dateTo,
                minDate: isBranchInventoryTab ? '' : filters.dateFrom,
                setDate: (date: string) => {
                    if (isBranchInventoryTab) return;
                    setFilters({ ...filters, dateTo: date });
                },
                startDate: isBranchInventoryTab ? '' : filters.dateFrom,
                endDate: isBranchInventoryTab ? '' : filters.dateTo,
                selectsEnd: true,
                disabled: isBranchInventoryTab,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName || '',
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => {
                    const selectedBranch = branchesList.find(branch =>
                        branch.branchName === event.target.value ||
                        branch.branchCode === event.target.value
                    );
                    setFilters({
                        ...filters,
                        branchCode: selectedBranch?.branchCode || '',
                        branchName: selectedBranch?.branchName || '',
                    });
                },
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branches found',
                className: Styles.input,
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Product Name:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedProductsListOption().sort((a, b) => a.value - b.value),
                value: filters.productName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    productName: event.target.value,
                    productId: productList.find((product) => product.productName === event.target.value)?.productId || 0,
                }),
                optionSelected: !!productList.find((product) => product.productName === filters.productName),
                noOptionsMessage: 'No product found',
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Gold],
                        value: MetalTypeEnum[MetalTypeEnum.Gold],
                    },
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Silver],
                        value: MetalTypeEnum[MetalTypeEnum.Silver],
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setFilters({
                        ...filters,
                        metalType: event.target.value
                    }),
                optionSelected: !!filters.metalType,
                noOptionsMessage: 'No metal type found',
            },
        },
        {
            label: 'Fund Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: FundTypeEnum[FundTypeEnum.Islamic],
                        value: FundTypeEnum.Islamic,
                    },
                    {
                        label: FundTypeEnum[FundTypeEnum.Conventional],
                        value: FundTypeEnum.Conventional,
                    },
                ],
                value: filters.fundType as keyof typeof FundTypeEnum,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, fundType: event.target.value }),
                optionSelected: !!FundTypeEnum[filters.fundType as keyof typeof FundTypeEnum],
                noOptionsMessage: 'No transaction type found',
                className: Styles.input,
            },
        },
        {
            label: 'Serial Number:',
            type: 'input' as const,
            inputParams: {
                value: filters.serialNo || '',
                setValue: (event) => setFilters({ ...filters, serialNo: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
                testId: 'serial-number-search-input'
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
            gridColumns={4}
        />
    );
};

export default InventoryReportSearchAndFilterBar;
